import React from 'react';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';
import { rgbToHex } from '@mui/material';

// Registracija potrebnih komponenti iz Chart.js
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, annotationPlugin);

// Mape boja za različite vrednosti "shift_desc"
const colorMap = {
  "PRE SMENE": "rgba(255, 99, 132, 0.6)",
  "U SMENI": "rgba(54, 162, 235, 0.6)",
  "POSLE SMENE": "rgba(75, 192, 192, 0.6)",
  "ODSUSTVO": "rgba(153, 102, 255, 0.6)",
  "IZLAZ RANIJE": "rgba(255, 159, 64, 0.6)",
  "PROVEDENO VREME": "rgba(255, 205, 86, 0.6)",
  "KASNJENJE": "rgba(201, 203, 207, 0.6)",
  "TRECA SMENA": "rgba(0, 128, 128, 0.6)",
  "IZLAZ SLUZBENI": "rgba(0, 255, 255, 0.6)",
  "IZLAZ VOZILOM": "rgba(0, 128, 0, 0.6)",
  "IZLAZ OPRAVDANI": "rgba(128, 0, 128, 0.6)",
  "IZLAZ LEKAR": "rgba(255, 0, 0, 0.6)",

};

function fillMissingDates(dates, startDate, endDate) {
  // Convert the dates to Date objects for easier manipulation
  const dateObjects = dates.map(date => new Date(date));

  // Parse the startDate and endDate into Date objects
  const parsedStartDate = new Date(startDate);
  console.log(endDate);

  const parsedEndDate = new Date(endDate);

  // Find the earliest and latest dates from the data, ensuring to include startDate and endDate
  const minDate = new Date(Math.min(...dateObjects, parsedStartDate));
  const maxDate = new Date(Math.max(...dateObjects, parsedEndDate));

  // Generate an array of all dates within the range (inclusive)
  const allDates = [];
  let currentDate = new Date(minDate);

  // Loop through the range, ensuring the last date is included
  while (currentDate <= maxDate) {
    const formattedDate = currentDate.toISOString().split('T')[0]; // yyyy-mm-dd
    allDates.push(formattedDate);
    currentDate.setDate(currentDate.getDate() + 1); // Move to the next day
  }
  const lastDate = parsedEndDate.toISOString().split('T')[0];
  if (!allDates.includes(lastDate)) {
    allDates.push(lastDate);
  }

  return allDates;
}




export const ReportChart = (employeeData, startDate, endDate) => {
  // Group data by day and add missing dates
  const initialGroupedData = employeeData.reduce((acc, entry) => {
    const day = entry.start.split(" ")[0]; // Extract date (yyyy-mm-dd)
    if (!acc[day]) acc[day] = [];
    acc[day].push(entry);
    return acc;
  }, {});

  // Fill in any missing dates with an empty array
  const allDates = fillMissingDates(Object.keys(initialGroupedData), startDate, endDate);
  const groupedData = allDates.reduce((acc, date) => {
    acc[date] = initialGroupedData[date] || []; // Assign existing data or empty array
    return acc;
  }, {});

  // Format data for Chart.js
  const chartData = {
    labels: allDates.map(day => day.slice(5)), // Get all labels in 'MM-DD' format
    datasets: []
  };

  const colorMapUsed = {};
  Object.keys(groupedData).forEach((day) => {
    let isFirstIteration = true;
    let prevEndHour = 0;

    groupedData[day].forEach((entry) => {
      const startHour = new Date(entry.start).getHours() + new Date(entry.start).getMinutes() / 60;
      const endHour = new Date(entry.end).getHours() + new Date(entry.end).getMinutes() / 60;
      const duration = endHour - startHour;

      if (!colorMapUsed[entry.shift_desc]) {
        colorMapUsed[entry.shift_desc] = colorMap[entry.shift_desc];
      }

      chartData.datasets.push({
        label: 'empty',
        data: [{ x: day.slice(5), y: startHour - prevEndHour, isVisible: false }],
        backgroundColor: 'rgba(0, 0, 0, 0)',
        borderWidth: 0,
        tooltip: { display: false },
      });

      chartData.datasets.push({
        label: entry.shift_desc,
        data: [{ x: day.slice(5), y: duration, yStart: startHour, yEnd: endHour, isVisible: true }],
        backgroundColor: colorMapUsed[entry.shift_desc],
        borderWidth: 0
      });

      prevEndHour = endHour;
    });
  });

  function indexToMin(index) {
    return index - 0.36;
  }

  function indexToMax(index) {
    return index + 0.36;
  }

  function middleValue(ctx, index, perc) {
    const chart = ctx.chart;
    const dataset = chart.data.datasets[0];
    return dataset.data[index] * perc;
  }

  // Add annotations for weekends
  const weekendAnnotation = allDates.map((day, index) => {
    const dayOfWeek = new Date(day).getDay();
    if (dayOfWeek === 6 || dayOfWeek === 0) { // Saturday or Sunday
      return {
        type: 'box',
        backgroundColor: 'rgba(255,255,0,0.2)',
        borderWidth: 0,
        label: {
          display: false,
          backgroundColor: 'rgba(255,255,0,0)',
          borderRadius: 0,
          color: 'white',
          content: 'Vikend'
        },
        xMax: indexToMax(index) + 0.14,
        xMin: indexToMin(index) - 0.14,
        xScaleID: 'x',
        yMax: (ctx) => middleValue(ctx, 0, 0.5),
        yMin: (ctx) => middleValue(ctx, 0, 0.5),
        yScaleID: 'y'
      };
    }
    return null;
  }).filter(annotation => annotation !== null);

  const options = {
    responsive: true,
    scales: {
      x: { 
        stacked: true,
      },
      y: {
        reverse: true,
        stacked: true,
        type: "linear",
        min: 0,
        max: 24,
        ticks: { stepSize: 1 },
      },
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: true,
        filter: (tooltipItem) => tooltipItem.dataset.label !== 'empty',
        callbacks: {
          label: (context) => {
            const entry = context.raw;
            if (!entry.yStart || !entry.yEnd) return `${context.dataset.label}: N/A`;
            const hours = `${Math.floor(entry.yStart)}:${(entry.yStart % 1 * 60).toFixed(0).padStart(2, '0')} - ${Math.floor(entry.yEnd)}:${(entry.yEnd % 1 * 60).toFixed(0).padStart(2, '0')}`;
            return `${context.dataset.label}: ${hours}`;
          }
        }
      },
      annotation: {
        annotations: weekendAnnotation
      }
    }
  };

  return <Bar data={chartData} options={options} />;
};

